import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";

/**
 * One client's invoice details (super admin only), opened from the Users page:
 * who they're billed as, where the invoice is addressed, and which account it's
 * paid into.
 *
 * The "From" block isn't typed here — it's an identity set up once in Settings,
 * and this only picks which one (or leaves it on the default). Everything is
 * read when an invoice is generated; an invoice already issued is frozen with
 * its own copy and never changes.
 */
const EMPTY = {
  invoice_identity_id: "",
  bill_to_text: "",
  bank_account_id: "",
  description: "",
  number_prefix: "",
  vat_label: "",
};

export default function InvoiceProfileModal({ open, client, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [banks, setBanks] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open || !client) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setSaved(false);
    (async () => {
      try {
        const [p, b, ids] = await Promise.all([
          api.get(`/clients/${client.id}/invoice-profile`),
          api.get("/bank-accounts"),
          api.get("/invoice-identities"),
        ]);
        if (cancelled) return;
        setBanks(b.data);
        setIdentities(ids.data);
        // A client with no identity of its own opens on the default one
        // already selected, rather than on a "use the default" option that
        // means the same thing as picking it.
        const fallback = ids.data.find((i) => i.is_default) || ids.data[0];
        setForm({
          invoice_identity_id: p.data.invoice_identity_id ?? (fallback ? fallback.id : ""),
          bill_to_text: p.data.bill_to_text || "",
          bank_account_id: p.data.bank_account_id ?? "",
          description: p.data.description || "",
          number_prefix: p.data.number_prefix || "",
          vat_label: p.data.vat_label || "",
        });
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load invoice details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, client]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open || !client) return null;

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.put(`/clients/${client.id}/invoice-profile`, {
        invoice_identity_id:
          form.invoice_identity_id === "" ? null : Number(form.invoice_identity_id),
        bill_to_text: form.bill_to_text || null,
        bank_account_id: form.bank_account_id === "" ? null : Number(form.bank_account_id),
        description: form.description || null,
        number_prefix: form.number_prefix || null,
        vat_label: form.vat_label || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save the invoice details");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 620 }}
      >
        <h3>Invoice details — {client.name}</h3>
        <p className="muted" style={{ marginTop: 8 }}>
          Used when an invoice is generated for a milestone on this client's project.
          The "From" block comes from an identity you set up once in Settings; the
          address below is printed line for line, first line bold.
          Already-generated invoices keep the details they were created with.
        </p>

        {loading && <div className="muted" style={{ marginTop: 12 }}>Loading…</div>}

        {!loading && (
          <>
            <div style={{ marginTop: 14 }}>
              <label className="field">Bill to (this client)</label>
              <textarea
                rows={4}
                value={form.bill_to_text}
                onChange={(e) => setForm((f) => ({ ...f, bill_to_text: e.target.value }))}
                placeholder={"CLIENT COMPANY LEGAL NAME\nStreet address\nCity, Province, Postal code\nCountry"}
                disabled={busy}
              />
            </div>

            <div style={{ marginTop: 10 }}>
              <label className="field">From (issued as)</label>
              <select
                value={form.invoice_identity_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, invoice_identity_id: e.target.value }))
                }
                disabled={busy || identities.length === 0}
              >
                {identities.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                    {i.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
              {identities.length === 0 && (
                <div className="muted-sm" style={{ marginTop: 4 }}>
                  No identities yet. Add one under Settings.
                </div>
              )}
            </div>

            <div style={{ marginTop: 10 }}>
              <label className="field">Bank account</label>
              <select
                value={form.bank_account_id}
                onChange={(e) => setForm((f) => ({ ...f, bank_account_id: e.target.value }))}
                disabled={busy}
              >
                <option value="">— none —</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                    {b.currency ? ` (${b.currency})` : ""}
                  </option>
                ))}
              </select>
              {banks.length === 0 && (
                <div className="muted-sm" style={{ marginTop: 6 }}>
                  No bank accounts yet. Add one under Settings.
                </div>
              )}
            </div>

            <div className="row-h" style={{ gap: 12, marginTop: 10 }}>
              <div style={{ flex: "0 0 130px" }}>
                <label className="field">Number prefix</label>
                <input
                  value={form.number_prefix}
                  onChange={(e) => setForm((f) => ({ ...f, number_prefix: e.target.value }))}
                  placeholder="MC"
                  disabled={busy}
                />
              </div>
              <div style={{ flex: "0 0 150px" }}>
                <label className="field">VAT line</label>
                <input
                  value={form.vat_label}
                  onChange={(e) => setForm((f) => ({ ...f, vat_label: e.target.value }))}
                  placeholder="VAT @ 0%"
                  disabled={busy}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="field">Line item description</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Software development and AI integration services"
                  disabled={busy}
                />
              </div>
            </div>
          </>
        )}

        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          {saved && <span className="check-msg">Saved</span>}
          <button type="button" onClick={onClose} disabled={busy}>Close</button>
          <button type="button" className="primary" onClick={save} disabled={busy || loading}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
